import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Loader2,
  MapPin,
  PackageCheck,
  RotateCcw,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropshipV2RequestError,
  dropshipV2ErrorMessage,
  type DropshipV2Field,
  type DropshipV2Plan,
  type DropshipV2Shell,
  useDropshipV2Capabilities,
  useDropshipV2Placement,
  useDropshipV2Shell,
  usePlaceDropshipV2POs,
  useRetryDropshipV2Processing,
} from "@/api/dropship-v2";
import { DropshipPOReviewCard } from "@/components/dropship-po-review-card";
import { LoadingButton } from "@/components/loading-button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatStatus } from "@/lib/utils";
import { DropshipDraftProgressPage } from "./dropship-draft-progress";
import { DropshipDraftReviewPage } from "./dropship-draft-review";

function money(value: unknown): string {
  return formatMoney(String(value ?? 0));
}

function fieldValue(fields: DropshipV2Field[], key: string): string {
  const value = fields.find((field) => field.field_key === key)?.value;
  return value == null ? "" : String(value);
}

function projectAddress(fields: DropshipV2Field[]): string {
  const street = [
    fieldValue(fields, "ship_to.address1"),
    fieldValue(fields, "ship_to.address2"),
  ]
    .filter(Boolean)
    .join(", ");
  const locality = [
    fieldValue(fields, "ship_to.city"),
    fieldValue(fields, "ship_to.state"),
    fieldValue(fields, "ship_to.zip"),
  ]
    .filter(Boolean)
    .join(" ");
  return [street, locality].filter(Boolean).join(" · ") || "-";
}

function ProjectReviewWorkflow({
  draftUuid,
  plan,
  fields,
  canPlace,
}: {
  draftUuid: string;
  plan: DropshipV2Plan;
  fields: DropshipV2Field[];
  canPlace: boolean;
}) {
  const navigate = useNavigate();
  const placement = usePlaceDropshipV2POs(draftUuid);
  const [attemptUuid, setAttemptUuid] = useState<string>();
  const attempt = useDropshipV2Placement(draftUuid, attemptUuid);
  // A manual-only plan has no PO to approve (USA-1388).
  const manualOnly = plan.proposed_pos.length === 0;
  const allReady = plan.proposed_pos.every((po) =>
    ["approved", "placed"].includes(po.state),
  );
  const totalLines = plan.proposed_pos.reduce(
    (total, po) => total + po.lines.length,
    0,
  );
  const supplierTotal = plan.proposed_pos.reduce(
    (total, po) => total + Number(po.total || 0),
    0,
  );
  const result = attempt.data?.result as Record<string, unknown> | undefined;
  const projectUuid =
    typeof result?.project_uuid === "string" ? result.project_uuid : undefined;
  const starting =
    ["pending", "in_progress"].includes(attempt.data?.status ?? "") ||
    placement.isPending;
  const failed = ["failed", "partial_failed"].includes(
    attempt.data?.status ?? "",
  );
  const attemptError = attempt.data?.error?.trim();
  const attemptErrorCode = attempt.data?.error_code?.trim();
  const remainingApprovalCount = plan.proposed_pos.filter(
    (po) => !["approved", "placed"].includes(po.state),
  ).length;
  const startBlocker = !canPlace
    ? "Project start is not enabled in this environment."
    : !allReady
      ? `${remainingApprovalCount} purchase order${remainingApprovalCount === 1 ? "" : "s"} still ${
          remainingApprovalCount === 1 ? "needs" : "need"
        } approval.`
      : null;

  useEffect(() => {
    if (attempt.data?.status !== "completed" || !projectUuid) return;
    void navigate({
      to: "/projects/$projectId",
      params: { projectId: projectUuid },
    });
  }, [attempt.data?.status, navigate, projectUuid]);

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <section className="min-w-0 overflow-hidden rounded-xl border bg-background xl:col-start-1">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Building2
            className="size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <h2 className="text-sm font-semibold">Project preview</h2>
        </div>
        <dl className="grid gap-x-8 gap-y-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">Project</dt>
            <dd className="mt-1 font-medium">
              {fieldValue(fields, "quote.project_name") || "Dropship project"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Job reference</dt>
            <dd className="mt-1 font-medium">
              {fieldValue(fields, "quote.job_reference_number") || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Customer</dt>
            <dd className="mt-1 font-medium">
              {fieldValue(fields, "customer.name") || "-"}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1 text-xs text-muted-foreground">
              <UserRound className="size-3.5" aria-hidden="true" /> Contact
            </dt>
            <dd className="mt-1 font-medium">
              {fieldValue(fields, "customer.contact_name") || "-"}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="size-3.5" aria-hidden="true" /> Deliver to
            </dt>
            <dd className="mt-1 font-medium">{projectAddress(fields)}</dd>
          </div>
        </dl>
      </section>

      <aside className="overflow-hidden rounded-xl border bg-background xl:col-start-2 xl:row-span-2 xl:row-start-1 xl:sticky xl:top-6">
        <div className="border-b px-4 py-4">
          <h2 className="text-base font-semibold">Start project</h2>
        </div>
        {manualOnly ? (
          <p className="px-4 py-4 text-sm text-muted-foreground">
            Every line is handled by hand, so no purchase orders are sent.
            Each line gets an action item when the project starts.
          </p>
        ) : (
          <dl className="space-y-3 px-4 py-4 text-sm">
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Purchase orders</dt>
              <dd className="font-medium tabular-nums">
                {plan.proposed_pos.length}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Equipment lines</dt>
              <dd className="font-medium tabular-nums">{totalLines}</dd>
            </div>
            <div className="flex items-center justify-between gap-4 border-t pt-3">
              <dt className="font-medium">Supplier total</dt>
              <dd className="font-semibold tabular-nums">
                {money(supplierTotal)}
              </dd>
            </div>
          </dl>
        )}
        <div className="border-t p-4">
          {attempt.data && failed ? (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 text-sm text-destructive"
            >
              <AlertCircle
                className="mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p>{attemptError || formatStatus(attempt.data.status)}</p>
                {attemptErrorCode ? (
                  <p className="mt-1 font-mono text-xs">
                    Error code: {attemptErrorCode}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          <LoadingButton
            type="button"
            className="w-full"
            disabled={!canPlace || !allReady || starting}
            loading={starting}
            loadingText="Starting project..."
            aria-describedby={
              startBlocker ? "start-project-blocker" : undefined
            }
            onClick={() =>
              placement.mutate(
                {
                  plan,
                  proposedPOs: plan.proposed_pos,
                  releaseSupplierActions: true,
                },
                {
                  onSuccess: (createdAttempt) =>
                    setAttemptUuid(createdAttempt.uuid),
                  onError: (error) =>
                    toast.error("Project was not started", {
                      description: dropshipV2ErrorMessage(
                        error,
                        "Review the approved purchase orders and try again.",
                      ),
                    }),
                },
              )
            }
          >
            <Building2 className="size-4" />
            {failed ? "Retry start project" : "Start project"}
          </LoadingButton>
          {startBlocker ? (
            <p
              id="start-project-blocker"
              className="mt-3 flex items-start gap-2 text-sm text-destructive"
            >
              <AlertCircle
                className="mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              {startBlocker}
            </p>
          ) : null}
        </div>
      </aside>

      <section className="min-w-0 overflow-hidden rounded-xl border bg-background xl:col-start-1">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <PackageCheck
              className="size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <h2 className="text-sm font-semibold">Purchase orders</h2>
          </div>
          <Badge variant="outline">{plan.proposed_pos.length}</Badge>
        </div>
        <div className="divide-y">
          {plan.proposed_pos.map((po) => (
            <div
              key={po.uuid}
              className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{po.manufacturer_name}</span>
                  <Badge variant="secondary">
                    {po.freight_rule_snapshot.we_book_freight
                      ? "USA-RS freight"
                      : "Manufacturer freight"}
                  </Badge>
                  <Badge variant="outline">
                    {formatStatus(
                      String(
                        po.spec_credit_decision.decision || "not_applicable",
                      ),
                    )}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {po.lines.length} line{po.lines.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex items-center justify-between gap-4 sm:justify-end">
                <Badge variant="outline">{formatStatus(po.state)}</Badge>
                <span className="min-w-24 text-right font-semibold tabular-nums">
                  {money(po.total)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ApprovalWorkflow({
  draftUuid,
  plan,
  canPMApprove,
  canPlace,
  fields,
  stage,
  onOperationsComplete,
}: {
  draftUuid: string;
  plan: DropshipV2Plan;
  canPMApprove: boolean;
  canPlace: boolean;
  fields: DropshipV2Field[];
  stage: "operations" | "project";
  onOperationsComplete: () => void;
}) {
  const [selectedPOUuid, setSelectedPOUuid] = useState<string>();
  const [activePOEditing, setActivePOEditing] = useState(false);
  const proposedPOs = useMemo(() => plan?.proposed_pos ?? [], [plan]);
  const fallbackPOUuid = proposedPOs[0]?.uuid;
  const activePOUuid = proposedPOs.some((po) => po.uuid === selectedPOUuid)
    ? selectedPOUuid
    : fallbackPOUuid;
  const activePO = proposedPOs.find((po) => po.uuid === activePOUuid);
  const operationsComplete = proposedPOs.every((po) =>
    ["approved", "placed"].includes(po.state),
  );

  if (stage === "project" || proposedPOs.length === 0) {
    return (
      <ProjectReviewWorkflow
        draftUuid={draftUuid}
        plan={plan}
        fields={fields}
        canPlace={canPlace}
      />
    );
  }

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[240px_minmax(0,1fr)]">
      <section
        aria-labelledby="po-worklist-title"
        data-po-worklist
        className="overflow-hidden rounded-xl border bg-background xl:sticky xl:top-6"
      >
        <div className="space-y-2 border-b px-3 py-3">
          <h2 id="po-worklist-title" className="text-sm font-semibold">
            PO checklist
          </h2>
          <Badge variant={operationsComplete ? "default" : "secondary"}>
            {
              proposedPOs.filter((po) =>
                ["approved", "placed"].includes(po.state),
              ).length
            }{" "}
            of {proposedPOs.length} approved
          </Badge>
        </div>
        {operationsComplete ? (
          <div className="flex items-center gap-2 border-b bg-emerald-50 px-3 py-3 text-xs font-medium text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
            <CheckCircle2 className="size-4" aria-hidden="true" /> Review
            complete
          </div>
        ) : null}
        {activePOEditing ? (
          <p className="border-b bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            Finish or cancel the active edit before switching POs.
          </p>
        ) : null}
        <ol
          className="max-h-64 divide-y overflow-y-auto xl:max-h-[calc(100dvh-11rem)]"
          aria-label="Generated purchase orders"
        >
          {proposedPOs.map((po) => {
            const active = po.uuid === activePOUuid;
            const switchBlocked = activePOEditing && !active;
            return (
              <li key={po.uuid} className={active ? "bg-primary/5" : undefined}>
                <div className="p-1.5">
                  <button
                    type="button"
                    aria-current={active ? "true" : undefined}
                    aria-disabled={switchBlocked}
                    className="grid min-h-14 w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                    onClick={() => {
                      if (switchBlocked) {
                        toast.info(
                          "Finish the active edit before switching POs",
                        );
                        return;
                      }
                      setSelectedPOUuid(po.uuid);
                    }}
                  >
                    <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums">
                      {po.display_order}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">
                        {po.manufacturer_name}
                      </span>
                      <span className="block text-xs text-foreground/70">
                        {po.lines.length} item{po.lines.length === 1 ? "" : "s"}{" "}
                        · {money(po.total)}
                      </span>
                      <Badge
                        variant={
                          ["approved", "placed"].includes(po.state)
                            ? "default"
                            : po.state === "needs_changes"
                              ? "destructive"
                              : "secondary"
                        }
                        className="mt-1"
                      >
                        {formatStatus(po.state)}
                      </Badge>
                    </span>
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
      {activePO ? (
        <DropshipPOReviewCard
          key={activePO.uuid}
          draftUuid={draftUuid}
          proposedPO={activePO}
          canApprove={canPMApprove}
          onEditingStateChange={setActivePOEditing}
          onApproved={() => {
            const nextPO = proposedPOs.find(
              (po) =>
                po.uuid !== activePO.uuid &&
                !["approved", "placed"].includes(po.state),
            );
            if (nextPO) {
              setSelectedPOUuid(nextPO.uuid);
            } else {
              onOperationsComplete();
            }
          }}
        />
      ) : null}
    </div>
  );
}

function ProcessingState({
  draftUuid,
  shell,
}: {
  draftUuid: string;
  shell: DropshipV2Shell;
}) {
  const retry = useRetryDropshipV2Processing(draftUuid);
  const failed = shell.processing_stage === "failed";
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start gap-3">
          {failed ? (
            <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
          ) : (
            <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary" />
          )}
          <div className="flex-1">
            <p className="font-semibold">
              {failed
                ? shell.source_kind === "ai_document"
                  ? "AI quote build stopped"
                  : "AQProj processing stopped"
                : formatStatus(shell.processing_stage)}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {failed
                ? shell.processing_error || "The source could not be processed."
                : "The page is available immediately. Header fields and line groups will appear as processing completes."}
            </p>
          </div>
        </div>
        <Progress value={shell.processing_progress} />
        <p className="text-right text-xs text-muted-foreground">
          {shell.processing_progress}%
        </p>
        {failed && (
          <LoadingButton
            variant="outline"
            loading={retry.isPending}
            loadingText="Retrying processing..."
            onClick={() =>
              retry.mutate(undefined, {
                onError: (error) =>
                  toast.error(dropshipV2ErrorMessage(error, "Retry failed")),
              })
            }
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Retry processing
          </LoadingButton>
        )}
      </CardContent>
    </Card>
  );
}

function DropshipDraftV2Page({
  draftUuid,
  shell,
}: {
  draftUuid: string;
  shell: DropshipV2Shell;
}) {
  return (
    <DropshipDraftReviewPage
      draftUuid={draftUuid}
      shell={shell}
      renderProcessing={() => (
        <ProcessingState draftUuid={draftUuid} shell={shell} />
      )}
      renderPlanWorkflow={({
        plan,
        canPMApprove,
        canPlace,
        fields,
        stage,
        onOperationsComplete,
      }) => (
        <ApprovalWorkflow
          draftUuid={draftUuid}
          plan={plan}
          canPMApprove={canPMApprove}
          canPlace={canPlace}
          fields={fields}
          stage={stage}
          onOperationsComplete={onOperationsComplete}
        />
      )}
    />
  );
}

function V2OrLegacyDraft({ draftUuid }: { draftUuid: string }) {
  const shell = useDropshipV2Shell(draftUuid);
  if (shell.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-36" />
      </div>
    );
  }
  if (
    !shell.data &&
    shell.error instanceof DropshipV2RequestError &&
    shell.error.status === 404
  ) {
    return <DropshipDraftProgressPage draftUuid={draftUuid} />;
  }
  if (!shell.data) {
    return (
      <div className="p-6">
        <Card className="border-destructive/30">
          <CardContent className="flex items-start gap-3 p-5 text-sm text-destructive">
            <AlertCircle className="h-5 w-5" />
            {dropshipV2ErrorMessage(
              shell.error,
              "This dropship draft could not be loaded.",
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  return <DropshipDraftV2Page draftUuid={draftUuid} shell={shell.data} />;
}

export function DropshipDraftVersionedPage({
  draftUuid,
}: {
  draftUuid: string;
}) {
  const capabilities = useDropshipV2Capabilities();
  if (capabilities.isLoading) {
    return <Skeleton className="m-6 h-40" />;
  }
  if (!capabilities.data?.intake_enabled) {
    return <DropshipDraftProgressPage draftUuid={draftUuid} />;
  }
  return <V2OrLegacyDraft draftUuid={draftUuid} />;
}
